import { Fragment } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import ChannelCell from './ChannelCell'
import { formatRevenueCompact } from './formatters'
import { type RequestComment } from './revisionRequest'
import RowNotes from './RowNotes'

export type Channel = { id: string; name: string }
export type Cell = { past: number; plan: number; actual?: number }
// Month labels for a revision's actual-vs-plan split; null for a plain version.
type PeriodLabels = { actualQuarters: string; quarters: string } | null

export type Props = {
  label: string
  depth: number
  channels: Channel[]
  currency?: string | null
  cells: Record<string, Cell>
  rowSum: number
  rowTarget: number
  onCellChange: (channelId: string, next: number) => void
  hasChildren?: boolean
  isCollapsed?: boolean
  onToggleCollapse?: () => void
  readOnly?: boolean
  onAutofill?: () => void
  autofillEnabled?: boolean
  onClearRow?: () => void
  clearEnabled?: boolean
  columnDeltaByChannel?: Record<string, number>
  // Open revision-request comments on this row, if any.
  notes?: RequestComment[]
  periodLabels?: PeriodLabels
}

export default function ChannelMatrixRow({
  label,
  depth,
  channels,
  currency,
  cells,
  rowSum,
  rowTarget,
  onCellChange,
  hasChildren = false,
  isCollapsed = false,
  onToggleCollapse,
  readOnly = false,
  onAutofill,
  autofillEnabled = false,
  onClearRow,
  clearEnabled = false,
  columnDeltaByChannel,
  notes,
  periodLabels,
}: Props) {
  const remaining = rowTarget - rowSum
  const actualRowSum = periodLabels
    ? channels.reduce((s, c) => s + (cells[c.id]?.actual ?? 0), 0)
    : 0
  const overshoot = remaining < 0
  const balanced = remaining === 0 && rowTarget > 0
  const deltaColor = overshoot
    ? 'var(--destructive, #c53030)'
    : balanced
      ? 'var(--success, var(--foreground))'
      : 'var(--muted-foreground)'
  const deltaLabel = overshoot
    ? `${formatRevenueCompact(Math.abs(remaining), currency)} over`
    : balanced
      ? 'Balanced'
      : `${formatRevenueCompact(remaining, currency)} remaining`
  const togglable = hasChildren && !!onToggleCollapse
  return (
    <Fragment>
      <tr>
        <td
          className={`sticky left-0 bg-background px-2 pt-1.5 pb-1.5 border-b border-border whitespace-nowrap z-10 ${notes?.length ? 'border-l-2 border-primary/40' : ''}`}
          style={{ paddingLeft: 8 + depth * 16 }}
        >
          <div className="flex items-start gap-1">
            {togglable ? (
              <button
                type="button"
                onClick={onToggleCollapse}
                title={isCollapsed ? 'Expand' : 'Collapse'}
                aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                aria-expanded={!isCollapsed}
                className="shrink-0 inline-flex items-center justify-center rounded transition-colors hover:bg-accent mt-0.5"
                style={{ width: 18, height: 18, color: 'var(--muted-foreground)' }}
              >
                {isCollapsed ? <ChevronRight size={14} strokeWidth={2} /> : <ChevronDown size={14} strokeWidth={2} />}
              </button>
            ) : (
              <div style={{ width: 18 }} aria-hidden />
            )}
            <div className="flex flex-col gap-0.5">
              <span style={{ color: 'var(--foreground)', fontWeight: readOnly ? 600 : hasChildren ? 500 : 400 }}>
                {label}
              </span>
              {!readOnly && (onAutofill || onClearRow) && (
                <div className="flex items-center gap-2 text-[11px]">
                  {onAutofill && (
                    <button
                      type="button"
                      onClick={autofillEnabled ? onAutofill : undefined}
                      disabled={!autofillEnabled}
                      title={hasChildren
                        ? (autofillEnabled
                          ? 'Fill channels where every child has data, using the sum of children'
                          : 'No channel has all children filled')
                        : 'Autofill only applies to nodes with children'}
                      className="underline-offset-2 hover:underline disabled:no-underline disabled:cursor-not-allowed"
                      style={{ color: autofillEnabled ? 'var(--link, var(--secondary))' : 'var(--muted-foreground)' }}
                    >
                      Autofill
                    </button>
                  )}
                  {onClearRow && (
                    <button
                      type="button"
                      onClick={clearEnabled ? onClearRow : undefined}
                      disabled={!clearEnabled}
                      title={clearEnabled ? 'Clear plan values on this row' : 'Nothing to clear'}
                      className="underline-offset-2 hover:underline disabled:no-underline disabled:cursor-not-allowed"
                      style={{ color: clearEnabled ? 'var(--link, var(--secondary))' : 'var(--muted-foreground)' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="px-2 pt-1.5 pb-1.5 border-b border-border align-top">
          <div className="flex flex-col gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground whitespace-nowrap">
            {periodLabels && <div className="flex items-center" style={{ height: 20 }}>{periodLabels.actualQuarters} Actual</div>}
            <div className="flex items-center" style={{ height: 20 }}>{periodLabels ? 'Last year' : 'Past'}</div>
            <div className="flex items-center" style={{ height: 24 }}>Growth %</div>
            <div className="flex items-center" style={{ height: 24 }}>{periodLabels ? `${periodLabels.quarters} Plan` : 'Plan'}</div>
            {periodLabels && <div className="flex items-center" style={{ height: 20 }}>Plan Total</div>}
          </div>
        </td>
        {channels.map((c) => {
          const cell = cells[c.id] ?? { past: 0, plan: 0, actual: 0 }
          return (
            <ChannelCell
              key={c.id}
              past={cell.past}
              plan={cell.plan}
              actual={cell.actual}
              showActual={!!periodLabels}
              currency={currency}
              onChange={(next) => onCellChange(c.id, next)}
              readOnly={readOnly}
              rowGap={rowTarget - rowSum}
              columnDelta={columnDeltaByChannel?.[c.id] ?? 0}
            />
          )
        })}
        <td
          className="sticky right-0 bg-background px-2 pt-1.5 pb-1.5 border-b border-border text-right tabular-nums z-10"
          style={{ color: deltaColor }}
        >
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Target: {formatRevenueCompact(rowTarget, currency)}
          </div>
          {deltaLabel}
          {periodLabels && (
            <div className="text-[10px] text-muted-foreground">
              Full year {formatRevenueCompact(actualRowSum + rowSum, currency)}
            </div>
          )}
        </td>
      </tr>
      {notes && notes.length > 0 && (
        <tr>
          <td colSpan={3 + channels.length} className="border-b border-border p-0">
            <RowNotes notes={notes} withTabBar />
          </td>
        </tr>
      )}
    </Fragment>
  )
}
